import React, { useEffect, useState, ReactNode, useCallback } from 'react';

interface SidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  width?: string;
  children: ReactNode;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  navLabel?: string;
}

const SidePanel: React.FC<SidePanelProps> = ({
  isOpen, onClose, title, width = '520px',
  children, onPrev, onNext, hasPrev, hasNext, navLabel,
}) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      // Двойной RAF: первый — браузер рисует панель в translateX(100%),
      // второй — только после этого запускаем transition в translateX(0)
      let raf1: number;
      let raf2: number;
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setVisible(true));
      });
      return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
    } else {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, handleKey]);

  if (!mounted) return null;

  const hasNav = onPrev !== undefined || onNext !== undefined;

  return (
    <>
      <div
        className={`side-panel-overlay${visible ? ' visible' : ''}`}
        onClick={onClose}
      />
      <aside
        className={`side-panel${visible ? ' open' : ''}`}
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="side-panel__header">
          <div className="side-panel__title-row">
            <h2 className="side-panel__title">{title}</h2>
            <button className="side-panel__close" onClick={onClose} aria-label="Закрыть">×</button>
          </div>
          {hasNav && (
            <div className="side-panel__nav">
              <button
                className="side-panel__nav-btn"
                onClick={onPrev}
                disabled={!hasPrev}
                title="Предыдущий (Alt+←)"
              >‹</button>
              {navLabel && <span className="side-panel__nav-label">{navLabel}</span>}
              <button
                className="side-panel__nav-btn"
                onClick={onNext}
                disabled={!hasNext}
                title="Следующий (Alt+→)"
              >›</button>
            </div>
          )}
        </div>
        <div className="side-panel__body">
          {children}
        </div>
      </aside>
    </>
  );
};

export default SidePanel;
